import React, { useMemo } from "react"
import { compact, get, isEmpty, map } from "lodash"
import SubsectionContainer from "./SubsectionContainer"
import { SubsectionContent, SubsectionContentList } from "./SubsectionContent"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { Provider } from "../../../domain/Provider/schema"
import { useLazyCmsCollectionMap } from "../../../domain/CMS/useLazyCmsCollectionMap"

interface ClientFocusContentProps {
  providers: Provider[]
}

interface ClientFocusData {
  ages: Record<string, boolean>
  languages: Record<string, boolean>
  ethnicity: Record<string, boolean>
  faithOrientation: Record<string, boolean>
  participants: Record<string, boolean>
  communities: Record<string, boolean>
}

const coalateClientFocusInfo = (providers: Provider[]) => {
  const clientFocusData: ClientFocusData = {
    ages: {},
    languages: {},
    ethnicity: {},
    faithOrientation: {},
    participants: {},
    communities: {},
  }
  for (const provider of providers) {
    if (isEmpty(provider)) {
      continue
    }

    if (provider.faithOrientationDisplayId) {
      clientFocusData.faithOrientation[provider.faithOrientationDisplayId] =
        true
    }

    for (const age of provider.providerAgesSeenDisplayIds) {
      clientFocusData.ages[age] = true
    }

    for (const language of provider.providerLanguageDisplayIds) {
      clientFocusData.languages[language] = true
    }

    for (const ethnicity of provider.providerClientEthnicityDisplayIds) {
      clientFocusData.ethnicity[ethnicity] = true
    }

    for (const participant of provider.providerParticipantDisplayIds) {
      clientFocusData.participants[participant] = true
    }

    for (const community of provider.providerCommunityDisplayIds) {
      clientFocusData.communities[community] = true
    }
  }

  return clientFocusData
}

export const ClientFocusContent: React.FC<ClientFocusContentProps> = ({
  providers,
}: ClientFocusContentProps) => {
  const { cms: cmsContent, isReady: isCMSReady } = useCMSReady([
    "provider",
    "providerCommunitiesServedMap",
    "providerLanguagesMap",
    "providerAgesSeen",
    "providerClientEthnicity",
    "providerFaithOrientation",
    "providerParticipant",
  ])
  const providerText = cmsContent.provider

  const resolvedAgesSeenMap = useLazyCmsCollectionMap(
    "agesSeen",
    "providerAgesSeen",
    "displayId",
  )
  const resolvedDemographicsMap = useLazyCmsCollectionMap(
    "clientEthnicities",
    "providerClientEthnicity",
  )
  const resolvedFaithOrientationMap = useLazyCmsCollectionMap(
    "faithOrientations",
    "providerFaithOrientation",
  )
  const resolvedParticipantMap = useLazyCmsCollectionMap(
    "participants",
    "providerParticipant",
    "displayId",
  )
  const providerCommunitiesServedMap = cmsContent.providerCommunitiesServedMap
  const providerLanguagesMap = cmsContent.providerLanguagesMap

  const clientFocusData = useMemo(() => {
    return isEmpty(providers) ? [] : coalateClientFocusInfo(providers)
  }, [providers])

  const clientFocusPanelsData = useMemo(() => {
    const isCmsContentLoaded =
      isCMSReady &&
      !isEmpty(cmsContent) &&
      !isEmpty(resolvedAgesSeenMap) &&
      !isEmpty(resolvedDemographicsMap) &&
      !isEmpty(resolvedFaithOrientationMap) &&
      !isEmpty(resolvedParticipantMap)
    if (isEmpty(providers) || !isCmsContentLoaded) {
      return [
        {
          subTitle: providerText?.demographicsAgesSeen,
          items: [],
        },
        {
          subTitle: providerText?.demographicsLanguages,
          items: [],
        },
        {
          subTitle: providerText?.demographicsEthnicities,
          items: [],
        },
        {
          subTitle: providerText?.demographicsFaith,
          items: [],
        },
        {
          subTitle: providerText?.demographicsParticipants,
          items: [],
        },
        {
          subTitle: providerText?.demographicsCommunitiesServed,
          items: [],
        },
      ]
    }

    const mapAndCompact = (
      cmsMap: Record<string, string>,
      clientFocusIndex: string,
      shouldSort: boolean = false,
    ) => {
      const ret = compact(
        map(
          cmsMap,
          (label, key) =>
            get(clientFocusData, [clientFocusIndex, key], null) && label,
        ),
      )
      if (shouldSort) {
        ret.sort((a, b) => (a && b ? a.localeCompare(b) : 0))
      }

      return ret
    }

    return [
      {
        subTitle: providerText?.demographicsAgesSeen,
        items: mapAndCompact(resolvedAgesSeenMap, "ages"),
      },
      {
        subTitle: providerText?.demographicsLanguages,
        items: mapAndCompact(
          providerLanguagesMap as Record<string, string>,
          "languages",
        ),
      },
      {
        subTitle: providerText?.demographicsEthnicities,
        items: mapAndCompact(resolvedDemographicsMap, "ethnicity", true),
      },
      {
        subTitle: providerText?.demographicsFaith,
        items: mapAndCompact(resolvedFaithOrientationMap, "faithOrientation"),
      },
      {
        subTitle: providerText?.demographicsParticipants,
        items: mapAndCompact(resolvedParticipantMap, "participants"),
      },
      {
        subTitle: providerText?.demographicsCommunitiesServed,
        items: mapAndCompact(
          providerCommunitiesServedMap as Record<string, string>,
          "communities",
        ),
      },
    ]
  }, [
    providers,
    cmsContent,
    isCMSReady,
    resolvedAgesSeenMap,
    resolvedDemographicsMap,
    resolvedFaithOrientationMap,
    resolvedParticipantMap,
    providerCommunitiesServedMap,
    providerLanguagesMap,
    clientFocusData,
    providerText,
  ])

  return (
    <SubsectionContainer>
      {clientFocusPanelsData.map(
        ({ subTitle, items }, index) =>
          items.length > 0 && (
            <SubsectionContent key={index} title={subTitle}>
              <SubsectionContentList listItems={items} />
            </SubsectionContent>
          ),
      )}
    </SubsectionContainer>
  )
}
